<template>
  <v-container>
    <v-tabs
      v-model="tab"
      slider-color="primary"
      color="secondary"
      background-color="transparent"
    >
      <v-tab>Données saisies</v-tab>
      <v-tab-item>
        <v-toolbar flat color="transparent" class="mt-5">
          <v-toolbar-title>
            Sélection
          </v-toolbar-title>
          <DataFetcher v-slot:default="{ items }" endpoint="/malls" class="mx-5">
            <v-select
              v-model="selectedMall"
              :items="items"
              :label="$t('Calendar.Label.mall')"
              item-text="name"
              color="primary"
              value="id"
              return-object
              hide-details
              prepend-inner-icon="business"
              style="max-width: 200px;"
            >
            </v-select>
          </DataFetcher>
          <BrandSelector
            v-if="selectedMall"
            v-model="selectedUser"
            :belongsToThisMall="selectedMall"
            style="max-width: 200px;"
          />
          <template v-if="selectedUser && firstEntryDate && lastPeriodDate">
            <v-divider vertical class="mx-5"></v-divider>
            <v-toolbar-title class="mr-5">
              Période
            </v-toolbar-title>
            <GeneratorYears
              v-slot:default="{years}"
              :startDate="firstEntryDate"
              :endDate="lastPeriodDate"
            >
              <v-select
                class="mr-5"
                v-model="selectedYear"
                :items="years.reverse()"
                :label="$t('Calendar.Label.year')"
                hide-details
                style="max-width: 125px;"
                prepend-inner-icon="calendar_today"
              ></v-select>
            </GeneratorYears>

            <GeneratorMonths
              v-if="selectedYear && selectedUser.inputFrequency === frequencyKeys.MENSUEL"
              v-slot:default="{months}"
              :startDate="firstEntryDate"
              :endDate="lastPeriodDate"
              :year="selectedYear"
            >
              <v-select
                v-model="selectedMonth"
                :items="months"
                :label="$t('Calendar.Label.month')"
                hide-details
                class="mr-5"
                style="max-width: 125px;"
                prepend-inner-icon="date_range"
              ></v-select>
            </GeneratorMonths>

            <GeneratorWeeks
              v-if="selectedYear && (selectedUser.inputFrequency === frequencyKeys.QUOTIDIEN || selectedUser.inputFrequency === frequencyKeys.HEBDOMADAIRE)"
              v-slot:default="{weeks}"
              :startDate="firstEntryDate"
              :endDate="lastPeriodDate"
              :year="selectedYear"
            >
              <v-select
                v-model="selectedWeek"
                :items="weeks"
                :label="$t('Calendar.Label.week')"
                hide-details
                style="max-width: 125px;"
                prepend-inner-icon="date_range"
              ></v-select>
            </GeneratorWeeks>
          </template>

        </v-toolbar>
        <v-slide-x-transition>
          <template v-if="(selectedMonth || selectedWeek) && !loading">
            <v-row justify="center" v-if="entries.length">
              <v-col cols="12">
                <EntryForm
                  :entries="entries"
                  :frequency="selectedUser.inputFrequency"
                  :user="selectedUser"
                  admin
                  @sent="initCurrentEntries"
                />
              </v-col>
            </v-row>
            <v-row justify="center" v-else>
              <v-col cols="8">
                <v-alert type="info">Aucune donnée saisie pour cette période</v-alert>
              </v-col>
            </v-row>
          </template>
        </v-slide-x-transition>
      </v-tab-item>
    </v-tabs>
  </v-container>
</template>

<script>
import Entries from '@/services/entries'
import EntryForm from '@/components/Entry/EntryForm'
import DataFetcher from '@/components/Utils/DataFetcher'
import BrandSelector from '@/components/Utils/BrandSelector'
import GeneratorYears from '@/components/Utils/GeneratorYears'
import GeneratorMonths from '@/components/Utils/GeneratorMonths'
import GeneratorWeeks from '@/components/Utils/GeneratorWeeks'
import { frequencyKeys } from '@/config/fixedValues';
import { endOfWeek, formatISO, startOfMonth, startOfWeek, getYear, parseISO } from 'date-fns'
import { mapState } from 'vuex'

export default {
  components: { EntryForm, DataFetcher, BrandSelector, GeneratorYears, GeneratorMonths, GeneratorWeeks },
  data: () => ({
    frequencyKeys,
    entries: [],
    tab: null,
    selectedMall: null,
    selectedUser: null,
    selectedYear: null,
    selectedMonth: null,
    selectedWeek: null,
    loading: false,
    firstEntryDate: null,
    lastPeriodDate: null
  }),

  computed: {
    ...mapState('auth', [
      'user'
    ]),
    frequency () {
      return this.selectedUser && this.selectedUser.inputFrequency
    },
  },
  watch: {
    async selectedWeek (value) {
      if (value) {
        this.loading = true
        this.loadEntries(await this.getEntries(value))
        this.loading = false
      }
    },
    async selectedMonth (value) {
      if (value) {
        this.loading = true
        this.loadEntries(await this.getEntries(value))
        this.loading = false
      }
    },
    selectedYear (value, oldValue) {
      if (oldValue) {
        this.selectedWeek = null
        this.selectedMonth = null
      }
    },
    selectedMall () {
      this.selectedUser = null
      this.selectedYear = null
      this.selectedWeek = null
      this.selectedMonth = null
    },
    async selectedUser (value) {
      // Trigger date change
      const prevYear = this.selectedYear;
      const prevWeek = this.selectedWeek;
      const prevMonth = this.selectedMonth;
      this.selectedYear = null
      this.selectedWeek = null
      this.selectedMonth = null

      if (value) {
        this.loading = true
        await this.initCurrentEntries()
        this.loading = false
      }

      // Re-select previous date if possible
      if (prevYear) {
        this.selectedYear = prevYear

        if (prevWeek && (this.frequency === frequencyKeys.QUOTIDIEN || this.frequency === frequencyKeys.HEBDOMADAIRE)) {
          this.selectedWeek = prevWeek
        }
        if (prevMonth && this.frequency === frequencyKeys.MENSUEL) {
          this.selectedMonth = prevMonth
        }
      }
    }
  },

  created () {
    this.init()
  },

  methods: {
    async init () {
      this.selectedMall = this.user.mall
    },
    /**
     * Get data for given date
     */
    async getEntries (startDate) {
      const endDate = this.frequency === frequencyKeys.QUOTIDIEN ? endOfWeek(startDate, {weekStartsOn: 1}) : startDate
      return await Entries.getUserEntriesByPeriod(this.selectedUser.id, {
        startDate: formatISO(startDate, { representation: 'date' }),
        endDate: formatISO(endDate, { representation: 'date' })
      })
    },

  /**
   * Hydrate entries before loading them in component
   */
    loadEntries (entries) {
      this.entries = entries.map(entry => ({
        ...entry,
        dateISO: parseISO(entry.date),
        disabled: !entry.isUserOpen,
        canComment: true
      }))
    },

    async initCurrentEntries () {
      if (!this.selectedUser.isActive) {
        this.entries = []
        const openingDate = parseISO(this.selectedUser.openingDate)
        const closingDate = parseISO(this.selectedUser.closingDate || new Date())
        this.firstEntryDate = this.frequency === frequencyKeys.MENSUEL ? startOfMonth(openingDate) : startOfWeek(openingDate, {weekStartsOn: 1})
        this.lastPeriodDate = this.frequency === frequencyKeys.MENSUEL ? startOfMonth(closingDate) : startOfWeek(closingDate, {weekStartsOn: 1})
        return
      }

      try {
        let currentEntries = await Entries.getCurrent(this.selectedUser.id)

        if (!currentEntries.length) {
          currentEntries = await Entries.generateNext(this.selectedUser.id)
        }

        this.loadEntries(currentEntries)

        // start date
        const openingDate = parseISO(this.selectedUser.openingDate)
        this.firstEntryDate = this.frequency === frequencyKeys.MENSUEL ? startOfMonth(openingDate) : startOfWeek(openingDate, {weekStartsOn: 1})

        // end date
        this.lastPeriodDate = this.entries[0].dateISO

        // auto selected year/month/week
        this.selectedYear = getYear(this.entries[0].dateISO)
        if (this.frequency === frequencyKeys.MENSUEL) this.selectedMonth = this.entries[0].dateISO
        if (this.frequency === frequencyKeys.QUOTIDIEN || this.inputFrequency === frequencyKeys.HEBDOMADAIRE) this.selectedWeek = this.entries[0].dateISO

      } catch (error) {
        this.$store.dispatch('errorMessage', 'Erreur lors de la récupération de la saisie courante')
      }
    },
  }
}
</script>

<style>
.theme--light.v-tabs-items {
  background-color: transparent;
}
</style>
